/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { BusinessType } from '../models/business-type';
import { CreateQuotationRequest } from '../models/create-quotation-request';
import { GenericResponse } from '../models/generic-response';
import { GetQuotationResponse } from '../models/get-quotation-response';
import { QuotationResponse } from '../models/quotation-response';
import { UpdateQuotationRequest } from '../models/update-quotation-request';

/**
 * Quotation APIs
 */
@Injectable({ providedIn: 'root' })
export class QuotationService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllQuotations()` */
  static readonly GetAllQuotationsPath = '/api/v1/quotations';

  /**
   * Get list of quotations.
   *
   * Gets the list of all the quotations with any of the filters applied. <br>
   * This list will contain all the revisions of every quotations.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllQuotations()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllQuotations$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * This adds a string search to the following fields:
       *   - quotationNumber
       */
      search?: string;

      /**
       * This adds a filter to the quotation list
       *  - Quotation type name
       */
      quotationTypeName?: BusinessType;

      /**
       * this adds a string search to the following fields:
       *   - client
       */
      clientId?: number;
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetQuotationResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      QuotationService.GetAllQuotationsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('search', params.search, {});
      rb.query('quotationTypeName', params.quotationTypeName, {});
      rb.query('clientId', params.clientId, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetQuotationResponse>;
        })
      );
  }

  /**
   * Get list of quotations.
   *
   * Gets the list of all the quotations with any of the filters applied. <br>
   * This list will contain all the revisions of every quotations.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllQuotations$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllQuotations(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * This adds a string search to the following fields:
       *   - quotationNumber
       */
      search?: string;

      /**
       * This adds a filter to the quotation list
       *  - Quotation type name
       */
      quotationTypeName?: BusinessType;

      /**
       * this adds a string search to the following fields:
       *   - client
       */
      clientId?: number;
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<GetQuotationResponse> {
    return this.getAllQuotations$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetQuotationResponse>): GetQuotationResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createQuotation()` */
  static readonly CreateQuotationPath = '/api/v1/quotations';

  /**
   * Create new quotation.
   *
   * This API creates a new quotation with provided fields, and a new quotation number.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createQuotation()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createQuotation$Response(
    params: {
      body: CreateQuotationRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<QuotationResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      QuotationService.CreateQuotationPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<QuotationResponse>;
        })
      );
  }

  /**
   * Create new quotation.
   *
   * This API creates a new quotation with provided fields, and a new quotation number.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createQuotation$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createQuotation(
    params: {
      body: CreateQuotationRequest;
    },
    context?: HttpContext
  ): Observable<QuotationResponse> {
    return this.createQuotation$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<QuotationResponse>): QuotationResponse => r.body
      )
    );
  }

  /** Path part for operation `getQuotationById()` */
  static readonly GetQuotationByIdPath = '/api/v1/quotations/{id}';

  /**
   * fetch quotation details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getQuotationById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getQuotationById$Response(
    params: {
      /**
       * The quotations id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<QuotationResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      QuotationService.GetQuotationByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<QuotationResponse>;
        })
      );
  }

  /**
   * fetch quotation details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getQuotationById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getQuotationById(
    params: {
      /**
       * The quotations id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<QuotationResponse> {
    return this.getQuotationById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<QuotationResponse>): QuotationResponse => r.body
      )
    );
  }

  /** Path part for operation `deleteQuotationById()` */
  static readonly DeleteQuotationByIdPath = '/api/v1/quotations/{id}';

  /**
   * Delete Quotation  by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteQuotationById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteQuotationById$Response(
    params: {
      /**
       * The Quotation id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      QuotationService.DeleteQuotationByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete Quotation  by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteQuotationById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteQuotationById(
    params: {
      /**
       * The Quotation id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteQuotationById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateQuotationById()` */
  static readonly UpdateQuotationByIdPath = '/api/v1/quotations/{id}';

  /**
   * Update quotation.
   *
   * This API will create a new version of a quotation, with the same quotation number and different revision <br>
   * number, if any of the fields provided differs from the original quotation or else this API would
   * return the original quotation.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateQuotationById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateQuotationById$Response(
    params: {
      /**
       * The quotations id that needs to be updated
       */
      id: number;
      body: UpdateQuotationRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<QuotationResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      QuotationService.UpdateQuotationByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<QuotationResponse>;
        })
      );
  }

  /**
   * Update quotation.
   *
   * This API will create a new version of a quotation, with the same quotation number and different revision <br>
   * number, if any of the fields provided differs from the original quotation or else this API would
   * return the original quotation.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateQuotationById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateQuotationById(
    params: {
      /**
       * The quotations id that needs to be updated
       */
      id: number;
      body: UpdateQuotationRequest;
    },
    context?: HttpContext
  ): Observable<QuotationResponse> {
    return this.updateQuotationById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<QuotationResponse>): QuotationResponse => r.body
      )
    );
  }
}
